import {
    CompleteIntermediate,
    CompleteTask,
    MiscEmployeeTaskGroup,
    MiscEmployeeTaskItem,
    ProjectComplete,
    ProjectPhaseAccountCode,
    ProjectPhaseWithAccountCodes,
} from "../interfaces/ProjectCompleteInterface";

/*
 * Pure filtering for the two breakdown cards on the Projects page.
 *
 * Matching is lowercase, trimmed, plain substring — no fuzzy matching, no word boundaries,
 * no regex built from user input. Each row is reduced to one flat searchable string whose
 * fields are joined by a separator, so a term can never bridge two fields.
 */

/** Field separator: contains characters a term can't produce by typing across two values. */
const SEP = " · ";

const searchable = (...parts: (string | null | undefined)[]): string =>
    parts.filter((p) => !!p).join(SEP).toLowerCase();

export const normalizeTerm = (term: string): string => term.trim().toLowerCase();

const fullName = (name?: { first?: string; last?: string } | null): string =>
    `${name?.first ?? ""} ${name?.last ?? ""}`.trim();

// ── Row search strings ───────────────────────────────────────────────────────

const nodeText = (n: CompleteIntermediate) => searchable(n.name, n.altIntermediateID);

const taskText = (t: CompleteTask) =>
    searchable(t.account?.altAccountCodeID, t.taskInfo?.name, t.taskInfo?.description);

/** Claims are the task's deep field — a hit here means "this person claimed this task". */
const taskClaimsText = (t: CompleteTask) =>
    (t.employeesClaimed ?? []).map((c) => searchable(fullName(c.name), c.altEmployeeID)).join(" | ");

const miscGroupText = (g: MiscEmployeeTaskGroup) =>
    searchable(g.accountCode?.altAccountCodeID, g.accountCode?.name);

const miscItemText = (i: MiscEmployeeTaskItem) =>
    searchable(
        fullName(i.employeeName),
        i.altEmployeeID,
        i.miscDescription,
        i.superDescription,
        i.intermediateName
    );

const phaseText = (p: ProjectPhaseWithAccountCodes) => searchable(p.name, p.altProjectPhaseID);

const codeText = (c: ProjectPhaseAccountCode) =>
    searchable(c.altAccountCodeID, c.name, c.description);

// ── Result shapes ────────────────────────────────────────────────────────────

/**
 * Why a container row is on screen:
 * - `match`   its own fields hit the term (subtle accent tint)
 * - `context` kept only because something beneath it matched (dimmed)
 * - `plain`   inside a matched subtree, which is revealed whole (no treatment)
 */
export type MatchMark = "match" | "context" | "plain";

export interface FilteredTask {
    task: CompleteTask;
    /** Matched on its own line — code, name or description. */
    self: boolean;
    /** A claimed employee matched, so the row auto-opens and the claim is visible. */
    viaClaim: boolean;
}

export interface FilteredNode {
    node: CompleteIntermediate;
    mark: MatchMark;
    tasks: FilteredTask[];
    children: FilteredNode[];
}

export interface FilteredMiscGroup {
    group: MiscEmployeeTaskGroup;
    mark: MatchMark;
    items: MiscEmployeeTaskItem[];
}

export interface TasksFilterResult {
    directTasks: FilteredTask[];
    nodes: FilteredNode[];
    miscGroups: FilteredMiscGroup[];
    /** Rows shown: task rows plus misc items. Compare against `countTaskRows`. */
    matches: number;
    /** Intermediates the filter must reveal. */
    revealNodes: number[];
    /** Task rows to auto-open (a claim matched). */
    revealTasks: number[];
    /** Misc groups to auto-open. */
    revealMisc: number[];
}

export interface FilteredPhase {
    phase: ProjectPhaseWithAccountCodes;
    mark: MatchMark;
    codes: ProjectPhaseAccountCode[];
}

export interface CodesFilterResult {
    phases: FilteredPhase[];
    /** Codes shown. Compare against `countPhaseCodes`. */
    matches: number;
    revealPhases: number[];
}

// ── Unfiltered totals (the M in "N of M" — never changes while typing) ───────

export const countTaskRows = (project: ProjectComplete): number => {
    const walk = (nodes: CompleteIntermediate[]): number =>
        nodes.reduce((s, n) => s + (n.tasks?.length ?? 0) + walk(n.intermediates ?? []), 0);
    const misc = (project.miscEmployeeTasks ?? []).reduce((s, g) => s + (g.tasks?.length ?? 0), 0);
    return (project.directTasks?.length ?? 0) + walk(project.intermediates ?? []) + misc;
};

export const countPhaseCodes = (phases: ProjectPhaseWithAccountCodes[]): number =>
    phases.reduce((s, p) => s + (p.accountCodes?.length ?? 0), 0);

// ── Tasks & Piece Work ──────────────────────────────────────────────────────

const flagTask = (task: CompleteTask, term: string): FilteredTask => ({
    task,
    self: taskText(task).includes(term),
    viaClaim: taskClaimsText(task).includes(term),
});

const allNodeIDs = (node: CompleteIntermediate, out: number[]): void => {
    out.push(node.intermediateID);
    (node.intermediates ?? []).forEach((c) => allNodeIDs(c, out));
};

/** Whole subtree, unfiltered: what a matching intermediate reveals. */
const keepWholeSubtree = (
    node: CompleteIntermediate,
    term: string,
    mark: MatchMark,
    acc: TasksFilterResult
): FilteredNode => {
    const tasks = (node.tasks ?? []).map((t) => flagTask(t, term));
    tasks.forEach((t) => {
        if (t.viaClaim) acc.revealTasks.push(t.task.taskID);
    });
    acc.matches += tasks.length;
    return {
        node,
        mark,
        tasks,
        children: (node.intermediates ?? []).map((c) => keepWholeSubtree(c, term, "plain", acc)),
    };
};

const walkNode = (
    node: CompleteIntermediate,
    term: string,
    acc: TasksFilterResult
): FilteredNode | null => {
    // "Show me everything under Building A" — the primary use of this filter.
    if (nodeText(node).includes(term)) {
        allNodeIDs(node, acc.revealNodes);
        return keepWholeSubtree(node, term, "match", acc);
    }

    const tasks = (node.tasks ?? [])
        .map((t) => flagTask(t, term))
        .filter((t) => t.self || t.viaClaim);
    const children = (node.intermediates ?? [])
        .map((c) => walkNode(c, term, acc))
        .filter((c): c is FilteredNode => c !== null);

    // Nothing matched anywhere beneath it → the intermediate is hidden entirely.
    if (tasks.length === 0 && children.length === 0) return null;

    // Kept as ancestor context for the hits below, and auto-expanded to expose them.
    acc.revealNodes.push(node.intermediateID);
    tasks.forEach((t) => {
        if (t.viaClaim) acc.revealTasks.push(t.task.taskID);
    });
    acc.matches += tasks.length;
    return { node, mark: "context", tasks, children };
};

const walkMiscGroup = (
    group: MiscEmployeeTaskGroup,
    term: string,
    acc: TasksFilterResult
): FilteredMiscGroup | null => {
    const groupHit = miscGroupText(group).includes(term);
    const items = groupHit
        ? group.tasks ?? []
        : (group.tasks ?? []).filter((i) => miscItemText(i).includes(term));
    if (items.length === 0) return null;

    // The group auto-opens either way — its items are the reason it survived.
    acc.revealMisc.push(group.accountCode.accountCodeID);
    acc.matches += items.length;
    return { group, mark: groupHit ? "match" : "context", items };
};

/** `term` must already be normalized. An empty term returns the tree untouched. */
export const filterTasksRegion = (project: ProjectComplete, term: string): TasksFilterResult => {
    const acc: TasksFilterResult = {
        directTasks: [],
        nodes: [],
        miscGroups: [],
        matches: 0,
        revealNodes: [],
        revealTasks: [],
        revealMisc: [],
    };

    if (!term) {
        acc.directTasks = (project.directTasks ?? []).map((t) => ({ task: t, self: false, viaClaim: false }));
        acc.nodes = (project.intermediates ?? []).map((n) => passThroughNode(n));
        acc.miscGroups = (project.miscEmployeeTasks ?? []).map((g) => ({
            group: g,
            mark: "plain" as MatchMark,
            items: g.tasks ?? [],
        }));
        return acc;
    }

    acc.directTasks = (project.directTasks ?? [])
        .map((t) => flagTask(t, term))
        .filter((t) => t.self || t.viaClaim);
    acc.directTasks.forEach((t) => {
        if (t.viaClaim) acc.revealTasks.push(t.task.taskID);
    });
    acc.matches += acc.directTasks.length;

    acc.nodes = (project.intermediates ?? [])
        .map((n) => walkNode(n, term, acc))
        .filter((n): n is FilteredNode => n !== null);

    acc.miscGroups = (project.miscEmployeeTasks ?? [])
        .map((g) => walkMiscGroup(g, term, acc))
        .filter((g): g is FilteredMiscGroup => g !== null);

    return acc;
};

const passThroughNode = (node: CompleteIntermediate): FilteredNode => ({
    node,
    mark: "plain",
    tasks: (node.tasks ?? []).map((t) => ({ task: t, self: false, viaClaim: false })),
    children: (node.intermediates ?? []).map(passThroughNode),
});

// ── Hourly Cost Codes ───────────────────────────────────────────────────────

/** `term` must already be normalized. An empty term returns the phases untouched. */
export const filterCodesRegion = (
    phases: ProjectPhaseWithAccountCodes[],
    term: string
): CodesFilterResult => {
    if (!term) {
        return {
            phases: phases.map((p) => ({ phase: p, mark: "plain", codes: p.accountCodes ?? [] })),
            matches: 0,
            revealPhases: [],
        };
    }

    const out: CodesFilterResult = { phases: [], matches: 0, revealPhases: [] };
    phases.forEach((phase) => {
        const phaseHit = phaseText(phase).includes(term);
        // Same "everything underneath" logic as a matching intermediate.
        const codes = phaseHit
            ? phase.accountCodes ?? []
            : (phase.accountCodes ?? []).filter((c) => codeText(c).includes(term));
        if (codes.length === 0) return;
        out.revealPhases.push(phase.projectPhaseID);
        out.matches += codes.length;
        out.phases.push({ phase, mark: phaseHit ? "match" : "context", codes });
    });
    return out;
};

/** Every intermediate ID in the tree — for Expand all. */
export const allIntermediateIDs = (nodes: CompleteIntermediate[]): number[] => {
    const out: number[] = [];
    nodes.forEach((n) => allNodeIDs(n, out));
    return out;
};
